import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, LoginRequest, AuthResponse, RegisterRequest, RegistrationResult, UpdateProfileRequest, EmailCodeLoginOptions, UpdateUserDetailsRequest, regionFromLocation } from '@radevents/shared';
import authService from '../services/authService';
import { userService } from '../services/userService';
import { redeemPendingFavorite } from '../services/pendingFavorite';
import { SignedInUserContext } from './signedInUser';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  register: (request: RegisterRequest) => Promise<RegistrationResult>;
  confirmEmail: (token: string) => Promise<AuthResponse>;
  resendConfirmation: (email: string) => Promise<void>;
  login: (credentials: LoginRequest) => Promise<AuthResponse>;
  requestEmailCode: (identifier: string) => Promise<void>;
  /**
   * `askDetails: false` unterdrückt die Frage nach den freiwilligen Angaben bei einem neuen Konto,
   * wo danach noch ein eigener Ablauf folgt (Veranstalter-Dialog).
   */
  loginWithEmailCode: (identifier: string, code: string, origin?: EmailCodeLoginOptions, opts?: { askDetails?: boolean }) => Promise<AuthResponse>;
  /** Ein neues Konto ist gerade entstanden: `AccountDetailsDialog` fragt einmal nach Geburtsjahr und Radarten. */
  detailsPromptOpen: boolean;
  closeDetailsPrompt: () => void;
  updateDetails: (data: UpdateUserDetailsRequest) => Promise<void>;
  /** Heimatregion setzen (Profil); `null` löscht sie. */
  updateRegion: (bundesland: string | null) => Promise<void>;
  /** Konto neu laden, nachdem ein anderer Weg es geändert hat (Mitmachen setzt die Region). */
  patchUser: (patch: Partial<User>) => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<AuthResponse | null>;
  isAdmin: () => boolean;
  isModerator: () => boolean;
  isAdminOrModerator: () => boolean;
  hasRole: (role: string) => boolean;
  updateLocation: (location: string) => Promise<void>;
  updateProfile: (data: UpdateProfileRequest) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [detailsPromptOpen, setDetailsPromptOpen] = useState(false);

  useEffect(() => {
    const clearStaleTokens = () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    };

    const initializeAuth = async () => {
      try {
        const refreshResult = await authService.refreshTokens();
        if (refreshResult) {
          setUser(refreshResult.user);
        } else {
          // No valid session — clear stale tokens so the Axios interceptor
          // doesn't send expired access tokens on public API calls (→ 401 → redirect)
          clearStaleTokens();
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        clearStaleTokens();
      } finally {
        setIsLoading(false);
      }
    };

    const handleAuthLogout = () => {
      setUser(null);
    };

    window.addEventListener('auth-logout', handleAuthLogout);

    initializeAuth();

    return () => {
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, []);

  // Meldet bewusst niemanden an: nach der Registrierung wartet der Bestaetigungslink.
  const register = async (request: RegisterRequest): Promise<RegistrationResult> =>
    authService.register(request);

  const confirmEmail = async (token: string): Promise<AuthResponse> => {
    const authResponse = await authService.confirmEmail(token);
    // Vor dem Login geparkten Merken-Klick einlösen, bevor die App den Auth-Wechsel rendert.
    await redeemPendingFavorite();
    setUser(authResponse.user);
    return authResponse;
  };

  const resendConfirmation = async (email: string): Promise<void> => {
    await authService.resendConfirmation(email);
  };

  const login = async (credentials: LoginRequest): Promise<AuthResponse> => {
    try {
      const authResponse = await authService.login(credentials);
      await redeemPendingFavorite();
      setUser(authResponse.user);
      return authResponse;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const requestEmailCode = async (identifier: string): Promise<void> => {
    await authService.requestEmailCode(identifier);
  };

  const loginWithEmailCode = async (
    identifier: string, code: string, origin?: EmailCodeLoginOptions, opts?: { askDetails?: boolean },
  ): Promise<AuthResponse> => {
    const authResponse = await authService.verifyEmailCode(identifier, code, origin);
    await redeemPendingFavorite();
    setUser(authResponse.user);
    if (authResponse.newAccount && opts?.askDetails !== false) setDetailsPromptOpen(true);
    return authResponse;
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
    }
  };

  const refreshToken = async (): Promise<AuthResponse | null> => {
    try {
      const authResponse = await authService.refreshTokens();
      if (authResponse) {
        setUser(authResponse.user);
      } else {
        setUser(null);
      }
      return authResponse;
    } catch (error) {
      console.error('Token refresh failed:', error);
      setUser(null);
      return null;
    }
  };

  const isAdmin = (): boolean => authService.isAdmin();
  const isModerator = (): boolean => authService.isModerator();
  const isAdminOrModerator = (): boolean => authService.isAdminOrModerator();
  const hasRole = (role: string): boolean => authService.hasRole(role);

  const updateLocation = async (location: string): Promise<void> => {
    let result = await userService.updateLocation(location);
    // Noch keine Region? Dann folgt sie aus der Postleitzahl. Eine gewählte bleibt, wie sie ist.
    const derived = result.bundesland ? null : regionFromLocation(location);
    if (derived) result = await userService.updateRegion(derived);
    setUser(result);
    localStorage.setItem('user', JSON.stringify(result));
  };

  const updateProfile = async (data: UpdateProfileRequest): Promise<void> => {
    const result = await authService.updateProfile(data);
    setUser(result);
  };

  const updateDetails = async (data: UpdateUserDetailsRequest): Promise<void> => {
    const result = await userService.updateDetails(data);
    setUser(result);
    localStorage.setItem('user', JSON.stringify(result));
  };

  const updateRegion = async (bundesland: string | null): Promise<void> => {
    const result = await userService.updateRegion(bundesland);
    setUser(result);
    localStorage.setItem('user', JSON.stringify(result));
  };

  const patchUser = (patch: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  // DSGVO-Selbstlöschung: nach dem Anonymisieren im Backend die lokale Session leeren.
  const deleteAccount = async (): Promise<void> => {
    await authService.deleteAccount();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    register,
    confirmEmail,
    resendConfirmation,
    login,
    requestEmailCode,
    loginWithEmailCode,
    logout,
    refreshToken,
    isAdmin,
    isModerator,
    isAdminOrModerator,
    hasRole,
    updateLocation,
    updateProfile,
    deleteAccount,
    detailsPromptOpen,
    closeDetailsPrompt: () => setDetailsPromptOpen(false),
    updateDetails,
    updateRegion,
    patchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      <SignedInUserContext.Provider value={value.isAuthenticated ? value.user : null}>
        {children}
      </SignedInUserContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
